import { RenderPass } from './renderpass.js';
import { MaterialVaryingFlags, RENDER_PASS_TYPE_LIGHT, QUEUE_OPAQUE, QUEUE_TRANSPARENT } from '../values.js';
import { Vector4 } from '@zephyr3d/base';
import { ShaderHelper } from '../material/shader/helper.js';
import { PostEffectLayer } from '../posteffect/posteffect.js';

const SURFACE_MRT_FLAGS = MaterialVaryingFlags.SSR_STORE_ROUGHNESS | MaterialVaryingFlags.SSS_STORE_PROFILE | MaterialVaryingFlags.SSS_STORE_DIFFUSE | MaterialVaryingFlags.SSS_STORE_NORMAL | MaterialVaryingFlags.SSS_STORE_TRANSMISSION;
const ADDITIVE_LIGHT_PASS_OMIT_MRT_FLAGS = MaterialVaryingFlags.SSR_STORE_ROUGHNESS | MaterialVaryingFlags.SSS_STORE_PROFILE | MaterialVaryingFlags.SSS_STORE_NORMAL;
/**
 * Forward render pass
 * @internal
 */ class LightPass extends RenderPass {
    /** @internal */ _shadowMapHash;
    /** @internal */ _transmission;
    /** @internal */ _renderOpaque;
    /** @internal */ _renderTransparent;
    /**
   * Creates an instance of ForwardRenderPass
   */ constructor(){
        super(RENDER_PASS_TYPE_LIGHT);
        this._shadowMapHash = null;
        this._transmission = false;
        this._renderOpaque = true;
        this._renderTransparent = true;
        this._clearColor = Vector4.zero();
    }
    /** @internal */ get transmission() {
        return this._transmission;
    }
    set transmission(val) {
        this._transmission = val;
    }
    /** @internal */ get renderOpaque() {
        return this._renderOpaque;
    }
    set renderOpaque(val) {
        this._renderOpaque = !!val;
    }
    /** @internal */ get renderTransparent() {
        return this._renderTransparent;
    }
    set renderTransparent(val) {
        this._renderTransparent = !!val;
    }
    /** @internal */ getAdditiveLightPassColorAttachments(ctx, materialFlags) {
        const framebuffer = ctx.device.getFramebuffer();
        if (!framebuffer) {
            return null;
        }
        const attachments = [
            framebuffer.getColorAttachments()[0]
        ];
        if (materialFlags & MaterialVaryingFlags.SSS_STORE_DIFFUSE) {
            attachments.push(ctx.SSSDiffuseTexture);
        }
        if (materialFlags & MaterialVaryingFlags.SSS_STORE_TRANSMISSION) {
            attachments.push(ctx.SSSTransmissionTexture);
        }
        return attachments.length === 1 ? attachments[0] : attachments;
    }
    /** @internal */ selectTransparentOITShadowLight(ctx, renderQueue) {
        const shadowMapInfo = ctx.shadowMapInfo;
        if (!shadowMapInfo || shadowMapInfo.size === 0) {
            return null;
        }
        if (renderQueue.primaryDirectionalLight && shadowMapInfo.has(renderQueue.primaryDirectionalLight)) {
            return renderQueue.primaryDirectionalLight;
        }
        if (renderQueue.primaryTransmissionLight && shadowMapInfo.has(renderQueue.primaryTransmissionLight)) {
            return renderQueue.primaryTransmissionLight;
        }
        for (const light of renderQueue.shadowedLights){
            if (shadowMapInfo.has(light)) {
                return light;
            }
        }
        return shadowMapInfo.keys().next().value ?? null;
    }
    /** @internal */ _getGlobalBindGroupHash(ctx, camera) {
        return `${this._shadowMapHash}:${ctx.currentShadowLight?.runtimeId ?? 0}:${ctx.lightBlending ? 1 : 0}:${camera.oit?.calculateHash() ?? ''}:${ctx.env.getHash(ctx)}:${ctx.materialFlags}:${ctx.linearDepthTexture?.uid ?? 0}:${ctx.sceneColorTexture?.uid ?? 0}:${ctx.HiZTexture?.uid ?? 0}`;
    }
    /** @internal */ renderLightPass(ctx, camera, itemList, lights, flags) {
        const baseLightPass = !ctx.lightBlending;
        ctx.drawEnvLight = baseLightPass && ctx.env.light.type !== 'none' && (ctx.env.light.envLight.hasRadiance() || ctx.env.light.envLight.hasIrradiance());
        ctx.renderPassHash = this.getGlobalBindGroupHash(ctx, camera);
        const bindGroup = ctx.globalBindGroupAllocator.getGlobalBindGroup(ctx);
        if (!flags.cameraSet[ctx.renderPassHash]) {
            ShaderHelper.setCameraUniforms(bindGroup, ctx, camera, !!ctx.device.getFramebuffer());
            flags.cameraSet[ctx.renderPassHash] = 1;
        }
        if (ctx.currentShadowLight) {
            ShaderHelper.setLightUniformsShadow(bindGroup, ctx, lights[0]);
        } else {
            if (!flags.lightSet[ctx.renderPassHash]) {
                ShaderHelper.setLightUniforms(bindGroup, ctx, ctx.clusteredLight.clusterParam, ctx.clusteredLight.countParam, ctx.clusteredLight.lightBuffer, ctx.clusteredLight.lightIndexTexture);
                flags.lightSet[ctx.renderPassHash] = 1;
            }
        }
        if (ctx.materialFlags & MaterialVaryingFlags.APPLY_FOG && !flags.fogSet[ctx.renderPassHash]) {
            ShaderHelper.setFogUniforms(bindGroup, ctx.env.sky.skyType === 'scatter' ? 1 : 0, ctx.env.sky.mappedFogType, baseLightPass ? 0 : 1, ctx.env.sky.atmosphereParams, ctx.env.sky.heightFogParams, ctx.env.sky.getAerialPerspectiveLUT(ctx), ctx.env.sky.getSkyDistantLightLUT(ctx));
            flags.fogSet[ctx.renderPassHash] = 1;
        }
        ctx.device.setBindGroup(0, bindGroup);
        const reverseWinding = camera.worldMatrixDet < 0;
        for (const lit of itemList.lit){
            this.drawItemList(lit, ctx, reverseWinding);
        }
        if (!ctx.lightBlending) {
            for (const unlit of itemList.unlit){
                this.drawItemList(unlit, ctx, reverseWinding);
            }
        }
    }
    /** @internal */ renderItems(ctx, camera, renderQueue) {
        ctx.renderPassHash = null;
        ctx.env = ctx.scene.env;
        ctx.drawEnvLight = false;
        ctx.flip = this.isAutoFlip(ctx);
        const surfaceMRT = ctx.materialFlags & SURFACE_MRT_FLAGS;
        const additiveOpaqueMaterialFlags = ctx.materialFlags & ~ADDITIVE_LIGHT_PASS_OMIT_MRT_FLAGS;
        const additiveTransparentMaterialFlags = ctx.materialFlags & ~SURFACE_MRT_FLAGS;
        const tmpFramebuffer = surfaceMRT ? ctx.device.pool.fetchTemporalFramebuffer(false, ctx.device.getDrawingBufferWidth(), ctx.device.getDrawingBufferHeight(), this.getAdditiveLightPassColorAttachments(ctx, additiveOpaqueMaterialFlags), ctx.device.getFramebuffer().getDepthAttachment()) : null;
        const needsTransparentSingleColorFramebuffer = !!tmpFramebuffer && tmpFramebuffer.getColorAttachments().length > 1;
        const transparentTmpFramebuffer = needsTransparentSingleColorFramebuffer ? ctx.device.pool.fetchTemporalFramebuffer(false, ctx.device.getDrawingBufferWidth(), ctx.device.getDrawingBufferHeight(), this.getAdditiveLightPassColorAttachments(ctx, additiveTransparentMaterialFlags), ctx.device.getFramebuffer().getDepthAttachment()) : tmpFramebuffer;
        const oit = renderQueue.drawTransparent && camera.oit && camera.oit.supportDevice(ctx.device.type) ? camera.oit : null;
        if (!oit && renderQueue.drawTransparent) {
            renderQueue.sortTransparentItems(camera.getWorldPosition());
        }
        const flags = {
            lightSet: {},
            cameraSet: {},
            fogSet: {}
        };
        const items = renderQueue.itemList;
        const lists = this._transmission ? [
            items?.transmission,
            items?.transmission_trans,
            items?.transparent
        ] : [
            items?.opaque,
            items?.transparent
        ];
        const transparentPass = lists.length - 1;
        for(let i = 0; i < lists.length; i++){
            const isOpaquePass = i === 0;
            if (isOpaquePass && !this._renderOpaque || !isOpaquePass && !this._renderTransparent) {
                continue;
            }
            if (lists[i]) {
                ctx.queue = i === 0 ? QUEUE_OPAQUE : QUEUE_TRANSPARENT;
                ctx.oit = i === 0 || !items ? null : oit;
                const isolateTransparentABufferLightPasses = !!ctx.oit && ctx.queue === QUEUE_TRANSPARENT && ctx.oit.getType() === 'ab';
                if ((ctx.queue === QUEUE_TRANSPARENT || this._transmission) && ctx.scene.env.sky.fogPresents) {
                    ctx.materialFlags |= MaterialVaryingFlags.APPLY_FOG;
                }
                const numOitPasses = ctx.oit ? ctx.oit.begin(ctx) : 1;
                for(let p = 0; p < numOitPasses; p++){
                    if (ctx.oit && !isolateTransparentABufferLightPasses) {
                        if (!ctx.oit.beginPass(ctx, p)) {
                            continue;
                        }
                    }
                    const runLightPass = (lights)=>{
                        if (ctx.oit && isolateTransparentABufferLightPasses) {
                            if (!ctx.oit.beginPass(ctx, p)) {
                                return;
                            }
                            this.renderLightPass(ctx, camera, lists[i], lights, flags);
                            ctx.oit.endPass(ctx, p);
                        } else {
                            this.renderLightPass(ctx, camera, lists[i], lights, flags);
                        }
                    };
                    const runAdditiveLightPass = (lights)=>{
                        const passFramebuffer = ctx.queue === QUEUE_TRANSPARENT ? transparentTmpFramebuffer : tmpFramebuffer;
                        if (ctx.lightBlending && passFramebuffer && !ctx.oit) {
                            const savedMaterialFlags = ctx.materialFlags;
                            ctx.materialFlags = ctx.queue === QUEUE_TRANSPARENT ? savedMaterialFlags & ~SURFACE_MRT_FLAGS : savedMaterialFlags & ~ADDITIVE_LIGHT_PASS_OMIT_MRT_FLAGS;
                            ctx.device.pushDeviceStates();
                            ctx.device.setFramebuffer(passFramebuffer);
                            try {
                                runLightPass(lights);
                            } finally{
                                ctx.materialFlags = savedMaterialFlags;
                                ctx.device.popDeviceStates();
                            }
                        } else {
                            runLightPass(lights);
                        }
                    };
                    let lightIndex = 0;
                    if (ctx.shadowMapInfo) {
                        for (const k of ctx.shadowMapInfo.keys()){
                            ctx.currentShadowLight = k;
                            ctx.lightBlending = lightIndex > 0;
                            this._shadowMapHash = ctx.shadowMapInfo.get(k).shaderHash;
                            runAdditiveLightPass([
                                k
                            ]);
                            lightIndex++;
                        }
                    }
                    if (lightIndex === 0 || renderQueue.unshadowedLights.length > 0) {
                        ctx.currentShadowLight = null;
                        ctx.lightBlending = lightIndex > 0;
                        this._shadowMapHash = '';
                        runAdditiveLightPass(renderQueue.unshadowedLights);
                    }
                    if (ctx.oit && !isolateTransparentABufferLightPasses) {
                        ctx.oit.endPass(ctx, p);
                    }
                }
                if (ctx.oit) {
                    ctx.oit.end(ctx);
                }
                ctx.materialFlags &= ~MaterialVaryingFlags.APPLY_FOG;
            }
            if (i === 0 && !ctx.sceneColorTexture) {
                if (tmpFramebuffer) {
                    ctx.device.pushDeviceStates();
                    ctx.device.setFramebuffer(tmpFramebuffer);
                }
                ctx.env.sky.renderSky(ctx);
                if (ctx.env.sky.fogPresents) {
                    ctx.env.sky.renderFog(camera);
                }
                if (tmpFramebuffer) {
                    ctx.device.popDeviceStates();
                }
            }
            if (!renderQueue.needSceneColor() || ctx.sceneColorTexture) {
                if (i === 0 || i === transparentPass) {
                    ctx.compositor?.drawPostEffects(ctx, i === 0 ? PostEffectLayer.opaque : PostEffectLayer.transparent, ctx.linearDepthTexture);
                    if (i === 0 && surfaceMRT && (ctx.device.getFramebuffer()?.getColorAttachments().length ?? 0) <= 1) {
                        ctx.materialFlags &= ~surfaceMRT;
                    }
                }
            }
        }
        if (tmpFramebuffer) {
            ctx.device.pool.releaseFrameBuffer(tmpFramebuffer);
        }
        if (transparentTmpFramebuffer && transparentTmpFramebuffer !== tmpFramebuffer) {
            ctx.device.pool.releaseFrameBuffer(transparentTmpFramebuffer);
        }
        ctx.oit = null;
    }
}

export { LightPass };
//# sourceMappingURL=lightpass.js.map
